import { Injectable } from '@angular/core';
import { AppEnvironment } from '../app.config';

export interface AiToolData {
    index: number;
    name: string;
    input: any;
    output: any;
}

export type MessageAuthor = 'user' | 'assistant' | 'system';

export interface ChatMessage {
    id: number | undefined;
    conversationId: string;
    sequence: number;
    timestamp: Date;
    author: MessageAuthor;
    message: string;
}

export interface ChatHistoryItem {
    role: MessageAuthor;
    content: string;
}

@Injectable({
    providedIn: 'root'
})
export class ChatService {

    async streamMessages(
        threadId: string,
        history: ChatHistoryItem[],
        msg: string,
        onChatChunk: (chunk: string) => void,
        onToolData: (tooldata : AiToolData) => void = () => {},
        abortSignal?: AbortSignal
    ) {
        const chatStreamUrl = `${AppEnvironment.chatServerBaseUrl}/api/stream/{threadId}`;
        const url = chatStreamUrl.replace('{threadId}', threadId);

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ chat_history: history, user_message: { message: msg } }),
                signal: abortSignal
            });

            if (!response.ok || !response.body) {
                throw new Error(`Failed to fetch chat stream: ${response.statusText}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let done = false;

            while (!done) {
                const { value, done: streamDone } = await reader.read();
                done = streamDone;

                const chunk = decoder.decode(value, { stream: !done });
                const lines = chunk.split('\n').filter(line => line.trim() !== '');

                for (const line of lines) {
                    try {
                        const json = JSON.parse(line);
                        if (json.type === 'model') {
                            onChatChunk(json.message);
                        } else if (json.type === 'tool_call') {
                            onToolData(json.message as AiToolData);
                        }
                    } catch (err) {
                        console.error('Error parsing JSON from chat stream', err, line);
                    }
                }
            }
        } catch (err) {
            console.error('Error in chat message streaming', err);
            throw err;
        }
    }
}
